import type { FC, ReactNode } from 'react'

import { media } from '@/constants/media'
import { Rule } from '@/components/generals/rule'
import { Reveal } from '@/components/generals/reveal'
import { SplitText } from '@/components/generals/split-text'
import { ScrollFigure } from '@/components/generals/scroll-figure'
import { studioMeta } from '@/constants/site'
import { awards } from '@/constants/studio'

/* A shelf, not a spreadsheet. The register keeps its ruled rows but now sits
   beside a plate and a short count, so the column widths carry some weight
   instead of four thin lines floating in an empty band. */
export const StudioAwards: FC = (): ReactNode => {
    return (
        <section
            id='studio-awards'
            aria-labelledby='studio-awards-title'
            className='relative z-10 bg-paper text-ink'
        >
            <div className='mx-auto flex max-w-[1920px] flex-col gap-10 px-5 sec-b lg:gap-14 lg:px-[33px]'>
                {/* Four paper sections run back to back on this page. A drawn
                    rule at the head of each is what stops them reading as one
                    continuous block. */}
                <Rule />

                <div className='grid gap-6 lg:grid-cols-4 lg:items-end lg:gap-5'>
                    <div className='flex items-start gap-3 lg:col-span-3'>
                        <SplitText
                            as='h2'
                            id='studio-awards-title'
                            text='Awards.'
                            stagger={0.03}
                            className='t-display text-ink'
                            style={{ fontSize: 'clamp(45px, 6vw, 80px)' }}
                        />

                        <Reveal from='up' distance={16} delay={0.25}>
                            <span className='t-mono text-ash'>
                                ({studioMeta.years})
                            </span>
                        </Reveal>
                    </div>

                    <Reveal from='up' distance={20} delay={0.2} className='flex items-end gap-3 lg:pb-2'>
                        <span className='t-display nums text-[clamp(34px,3.4vw,44px)] leading-none text-brand'>
                            {awards.length}
                        </span>

                        <span className='t-mono max-w-[18ch] leading-[1.6] text-ash'>
                            Named pieces, judged by people who build for a living
                        </span>
                    </Reveal>
                </div>

                <div className='grid gap-10 lg:grid-cols-4 lg:items-start lg:gap-5'>
                    <div className='flex flex-col lg:col-span-3'>
                        <div className='grid grid-cols-4 gap-3 pb-4 lg:gap-5'>
                            <span className='t-mono text-ash'>
                                Awarded by
                            </span>

                            <span className='t-mono col-span-2 text-ash'>
                                Award
                            </span>

                            <span className='t-mono text-right text-ash'>
                                year
                            </span>
                        </div>

                        <Rule />

                        <Reveal
                            from='up'
                            distance={30}
                            stagger={0.1}
                            childSelector='[data-award-row]'
                            className='flex flex-col'
                        >
                            {awards.map((award, index) => (
                                <div key={`${award.by}-${award.year}`} className='group/award'>
                                    <div
                                        data-award-row
                                        className='grid grid-cols-4 items-baseline gap-3 py-5 transition-[padding-left] duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] lg:gap-5 lg:group-hover/award:pl-3'
                                    >
                                        <span className='t-cond text-[15px] leading-[1.2] transition-colors duration-500 group-hover/award:text-brand lg:text-[19px]'>
                                            {award.by}
                                        </span>

                                        <span className='t-cond-soft col-span-2 text-[15px] leading-[1.2] lg:text-[17px]'>
                                            {award.award}
                                        </span>

                                        <time
                                            dateTime={award.year}
                                            className='t-cond-soft nums text-right text-[15px] leading-[1.2] text-ash lg:text-[17px]'
                                        >
                                            {award.year}
                                        </time>
                                    </div>

                                    <Rule delay={0.1 + index * 0.1} />
                                </div>
                            ))}
                        </Reveal>
                    </div>

                    <ScrollFigure
                        src={media.editorial.bookSpread}
                        alt='An awarded brand book open on the studio table'
                        aspect='aspect-[4/5]'
                        drift={6}
                        index='(A)'
                        caption='Kestrel brand book, 2025'
                        className='lg:order-first'
                    />
                </div>
            </div>
        </section>
    )
}
